import { Center } from '@folio/components/ui/flex';
import { useCalculatePolicyQuote } from '@folio/hooks';
import { QuoteContext } from '@folio/modules/shared-quote/QuoteContext';
import { withTierRequiredPerils } from '@folio/modules/shared-quote/use-calculate-quotes';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatPrice,
} from '@korint/folio';
import { useQuery } from '@tanstack/react-query';
import {
  type BrokerageFeeOnPolicy,
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  type ConfigQuoteChosenWarrantyDto,
  getBrokerageFirmOutputFirmTypeEnum,
  invoiceFrequencyEnum,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';
import { useDebouncedValue } from 'korint-sdk/react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { Loader2Icon } from 'lucide-react';
import { useParams } from 'react-router-dom';
import {
  ImaAssistanceComponentIds,
  baseDrivequantScoreRange,
  bestDrivequantScoreRange,
} from '../configs/constants';

function toNumberOrUndefined(value: unknown) {
  return value === undefined || value === null || value === '' ? undefined : Number(value);
}

function buildBrokerageFeesOverride(
  policyBrokerageFees: BrokerageFeeOnPolicy[],
  retailBrokerageFirmId: string | undefined,
  commissionRate: number | undefined,
) {
  if (commissionRate === undefined || !retailBrokerageFirmId) return undefined;

  const otherFees = policyBrokerageFees.filter(
    ({ brokerageFirmId }) => brokerageFirmId !== retailBrokerageFirmId,
  );

  return [...otherFees, { brokerageFirmId: retailBrokerageFirmId, fee: commissionRate }];
}

function useDrivequantQuote({
  policyId,
  branchId,
  assetId,
  tier,
  perils,
  scoreRange,
  managementFeeTaxesIncluded,
  brokerageFeesOverride,
  retailBrokerageFirmId,
  enabled,
}: {
  policyId: string;
  branchId?: string;
  assetId?: string;
  tier?: string;
  perils: ConfigQuoteChosenWarrantyDto[];
  scoreRange: string;
  managementFeeTaxesIncluded: number;
  brokerageFeesOverride?: BrokerageFeeOnPolicy[];
  retailBrokerageFirmId?: string;
  enabled: boolean;
}) {
  return useCalculatePolicyQuote(
    {
      policyId,
      branchId,
      quoteOptions: {
        assetIds: [assetId!],
        options: [{ assetId: assetId!, tier: tier!, excess: 'DEFAULT', perils }],
        customFieldsToOverride: [
          { key: 'oldDrivequantScoreRange', value: scoreRange },
          { key: 'newDrivequantScoreRange', value: scoreRange },
          { key: 'managementFeeTaxesIncluded', value: managementFeeTaxesIncluded },
        ],
        brokerageFeesOverride,
        prorated: true,
      },
    },
    {
      silenceErrors: true,
      enabled: enabled && !!assetId && !!tier && !!brokerageFeesOverride?.length,
      select: data => {
        const findPolicyComponentAmount = (componentId: string) =>
          data?.policy?.components.find(({ id }) => id === componentId)?.premium
            ?.amountWithFeesAndTaxes ?? 0;
        const retailManagementFee = findPolicyComponentAmount('RETAIL_MANAGEMENT_FEE');

        return {
          ...data?.assets?.[0]?.totalPremium,
          totalAssistance:
            data?.assets?.[0]?.components.find(({ id }) => ImaAssistanceComponentIds.includes(id))
              ?.premium?.amountWithFeesAndTaxes ?? 0,
          retailFees: data?.assets?.[0]?.totalPremium?.fees
            .filter(({ brokerageFirmId }) => brokerageFirmId === retailBrokerageFirmId)
            .reduce((acc, { amount }) => acc + (amount ?? 0), 0),
          retailManagementFee,
          totalManagementFee:
            findPolicyComponentAmount('WHOLESALE_MANAGEMENT_FEE') + retailManagementFee,
        };
      },
    },
  );
}

export function DriveQuantComparison() {
  const { t } = useTranslation();
  const { policy, asset, quoteConfig } = useContext(QuoteContext);
  const { branchId } = useParams<{ branchId: string }>();
  const form = useFormContext();
  const formValues = useWatch({ control: form.control });

  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: { firmType } = {} } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(user?.brokerageFirmId!),
    enabled: !!user?.brokerageFirmId,
  });

  // Debounced to avoid Wakam rate limits while typing
  const commissionRate = toNumberOrUndefined(useDebouncedValue(formValues.commissionRate));
  const managementFeeTaxesIncluded =
    Number(useDebouncedValue(formValues.managementFeeTaxesIncluded) ?? 0) * 100;

  const selectedTier = formValues.tier as string | undefined;
  const tierPerils =
    quoteConfig?.availableConfigs.find(config => config.tier === selectedTier)?.perils ?? [];
  const chosenPerils: ConfigQuoteChosenWarrantyDto[] = Object.entries(formValues)
    .filter(([key, value]) => key.startsWith('peril-') && value !== 'false')
    .map(([key, value]) => ({
      id: key.replace('peril-', ''),
      ...(value === 'true' ? {} : { option: value as string }),
    }));
  // Tier and perils debounced together, as a string: useDebouncedValue compares by reference
  const { tier, perils }: { tier?: string; perils: ConfigQuoteChosenWarrantyDto[] } = JSON.parse(
    useDebouncedValue(
      JSON.stringify({
        tier: selectedTier,
        perils: withTierRequiredPerils(chosenPerils, tierPerils).sort((a, b) =>
          a.id.localeCompare(b.id),
        ),
      }),
      300,
    ),
  );

  const retailBrokerageFirmId = policy?.brokerageFirm;
  const brokerageFeesOverride = buildBrokerageFeesOverride(
    policy?.brokerageFees ?? [],
    retailBrokerageFirmId,
    commissionRate,
  );

  const quoteParams = {
    policyId: policy?.id!,
    branchId,
    assetId: asset?.id,
    tier,
    perils,
    managementFeeTaxesIncluded,
    brokerageFeesOverride,
    retailBrokerageFirmId,
    enabled: !!policy?.id && !form.formState.isSubmitting,
  };

  const { data: baseQuote, isLoading: isBaseLoading } = useDrivequantQuote({
    ...quoteParams,
    scoreRange: baseDrivequantScoreRange,
  });
  const { data: bestQuote, isLoading: isBestLoading } = useDrivequantQuote({
    ...quoteParams,
    scoreRange: bestDrivequantScoreRange,
  });

  if (!policy?.id || !asset?.id) return null;

  const isLoading = isBaseLoading || isBestLoading || form.formState.isSubmitting;
  const isWholesale = firmType === getBrokerageFirmOutputFirmTypeEnum.WHOLESALE;
  const frequency =
    policy.invoicingConfig.frequency === invoiceFrequencyEnum.MONTHLY ? 'monthly' : 'weekly';
  const format = (amount: number | undefined) =>
    amount !== undefined ? formatPrice(amount) : t('axeceVtc.drivequantComparison.unavailable');
  const quotes = [baseQuote, bestQuote];
  const rows = [
    { key: 'preTaxesPremium', amount: (quote: typeof baseQuote) => quote?.amountWithFeesPreTaxes },
    { key: 'taxes', amount: (quote: typeof baseQuote) => quote?.totalTaxes },
    { key: 'totalPremium', amount: (quote: typeof baseQuote) => quote?.amountWithFeesAndTaxes },
    { key: 'assistance', amount: (quote: typeof baseQuote) => quote?.totalAssistance },
    {
      key: 'commissions',
      amount: (quote: typeof baseQuote) => (isWholesale ? quote?.totalFees : quote?.retailFees),
    },
    {
      key: 'managementFee',
      amount: (quote: typeof baseQuote) =>
        isWholesale ? quote?.totalManagementFee : quote?.retailManagementFee,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(`axeceVtc.drivequantComparison.title.${frequency}`)}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 text-sm">
        <div className="relative">
          {isLoading && (
            <Center
              role="status"
              aria-live="polite"
              className="z-10 absolute top-0 left-0 w-full h-full bg-white"
            >
              <Loader2Icon className="h-7 w-7 animate-spin" aria-hidden="true" />
              <span className="sr-only">{t('axeceVtc.drivequantComparison.loading')}</span>
            </Center>
          )}
          <Table aria-label={t('axeceVtc.drivequantComparison.tableLabel')} className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead />
                <TableHead className="text-right">
                  {t('axeceVtc.drivequantComparison.columns.base')}
                </TableHead>
                <TableHead className="text-right">
                  {t('axeceVtc.drivequantComparison.columns.minimum')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableHead scope="row">{t('axeceVtc.drivequantComparison.rows.score')}</TableHead>
                <TableCell className="text-right">
                  {t('axeceVtc.drivequantComparison.scores.base')}
                </TableCell>
                <TableCell className="text-right">
                  {t('axeceVtc.drivequantComparison.scores.best')}
                </TableCell>
              </TableRow>
              {rows.map(({ key, amount }) => (
                <TableRow key={key}>
                  <TableHead scope="row">
                    {t(`axeceVtc.drivequantComparison.rows.${key}`)}
                  </TableHead>
                  {quotes.map((quote, index) => (
                    <TableCell key={index} className="text-right">
                      {format(amount(quote))}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableHead scope="row">{t('axeceVtc.drivequantComparison.rows.total')}</TableHead>
                {quotes.map((quote, index) => (
                  <TableCell key={index} className="text-right text-secondary font-semibold">
                    {format(
                      quote
                        ? (quote.amountWithFeesAndTaxes ?? 0) + quote.totalManagementFee
                        : undefined,
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <p>{t('axeceVtc.drivequantComparison.scoreEvolution')}</p>
        <div>
          {t('axeceVtc.drivequantComparison.progressiveDiscount')}
          <ul className="list-disc pl-6">
            <li>{t('axeceVtc.drivequantComparison.progressiveDiscountSteps')}</li>
            <li>{t('axeceVtc.drivequantComparison.progressiveDiscountCondition')}</li>
          </ul>
        </div>
        <p>{t('axeceVtc.drivequantComparison.minimumPrice')}</p>
        <p>{t('axeceVtc.drivequantComparison.surcharge')}</p>
        <p>{t('axeceVtc.drivequantComparison.averageScore')}</p>
      </CardContent>
    </Card>
  );
}
